/**
 * Generates an array of month names using the native Intl API.
 * @param locale - The locale to use (default: 'en-NZ')
 * @param format - The format: 'long' (January), 'short' (Jan), or 'narrow' (J)
 * @returns Array of month names (0-indexed: January = 0)
 */
export const getMonthNames = (
    locale: string = 'en-NZ',
    format: 'long' | 'short' | 'narrow' = 'long'
): string[] => {
    const formatter = new Intl.DateTimeFormat(locale, { month: format });
    // Create dates for each month of the year
    return Array.from({ length: 12 }, (_, i) => {
        const date = new Date(2024, i, 1);
        return formatter.format(date);
    });
};

/**
 * Month names for calendar components (long format)
 * Uses native Intl API for consistency and localization support.
 */
export const MONTH_NAMES = getMonthNames('en-NZ', 'long');

/**
 * Generates an array of weekday names using the native Intl API.
 * @param locale - The locale to use (default: 'en-NZ')
 * @param format - The format: 'long' (Monday), 'short' (Mon), or 'narrow' (M)
 * @returns Array of weekday names starting from Sunday (index 0)
 */
export const getWeekdayNames = (
    locale: string = 'en-NZ',
    format: 'long' | 'short' | 'narrow' = 'long'
): string[] => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: format });
    // Create dates for each day of the week (starting from a known Sunday: 2024-01-07)
    return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(2024, 0, 7 + i); // 2024-01-07 is a Sunday
        return formatter.format(date);
    });
};

/**
 * Rotates an array to start from Monday instead of Sunday
 * @param weekdays - Array of weekday names
 * @returns Array starting from Monday
 */
const rotateToMonday = (weekdays: string[]): string[] => {
    if (weekdays.length === 0) return weekdays;

    // Find Monday's index
    const mondayIndex = weekdays.findIndex((day) =>
        day.toLowerCase().startsWith('mon')
    );

    // If Monday not found or already at start, return as-is
    if (mondayIndex <= 0) return weekdays;

    // Rotate array to start from Monday
    return [...weekdays.slice(mondayIndex), ...weekdays.slice(0, mondayIndex)];
};

/**
 * Short day names starting from Sunday (0) to Saturday (6)
 * Useful for components that use Date.getDay() which returns 0 for Sunday
 */
export const DAY_NAMES_SHORT = getWeekdayNames('en-NZ', 'short');

/**
 * Day names starting from Monday to Sunday
 * Useful for calendar grids that start the week on Monday
 */
export const DAY_NAMES_WEEK_START_MONDAY = rotateToMonday(
    getWeekdayNames('en-NZ', 'short')
);

/**
 * Weekday names in English (long format).
 * Uses native Intl API for consistency and localization support.
 */
export const WEEKDAY_NAMES = getWeekdayNames('en-NZ', 'long');

/**
 * Maps weekday names to their index (0 = Sunday, 6 = Saturday).
 * Dynamically generated from WEEKDAY_NAMES.
 */
export const WEEKDAY_INDEX: Record<string, number> = WEEKDAY_NAMES.reduce(
    (acc, name, index) => {
        acc[name] = index;
        return acc;
    },
    {} as Record<string, number>
);

/**
 * Calendar and booking constants
 */

/** Maximum days to look ahead when searching for the next available date in calendar components */
export const MAX_LOOKAHEAD_DAYS = 62;

/** Number of days to load calendar events for booking availability (current month + next month + 1 day) */
export const LOOKAHEAD_DAYS = 62;

/** Maximum days to load on client (current month + next month + 1 day) */
export const CLIENT_LOOKAHEAD_DAYS = 62;

/** Initial days to load - same as full range since we only support 2 months */
export const INITIAL_LOOKAHEAD_DAYS = 62;

/** Workday interval in minutes for booking slots */
export const WORKDAY_INTERVAL_MINUTES = 15;

/** Workday start hour (24-hour format) */
export const WORKDAY_START_HOUR = 0;

/** Workday end hour (24-hour format) - allows slots up to 10:00 PM (includes 9:30 PM) */
export const WORKDAY_END_HOUR = 24;

/**
 * Supported timezone configuration
 */
export interface TimezoneInfo {
    name: string;
    displayName: string;
    timeZoneId: string;
    /**
     * Get formatted display name with timezone abbreviation.
     * @param date - Optional date for DST-aware formatting
     */
    getFormattedDisplayName?: (date?: Date) => string;
}

export type SupportedTimezone = 'NZ' | 'AU' | 'US';

/** Supported timezones for calendar and booking components */
export const SUPPORTED_TIMEZONES: Record<SupportedTimezone, TimezoneInfo> = {
    NZ: {
        name: 'NZ',
        displayName: 'New Zealand',
        timeZoneId: 'Pacific/Auckland',
    },
    AU: {
        name: 'AU',
        displayName: 'Australia (Sydney)',
        timeZoneId: 'Australia/Sydney',
    },
    US: {
        name: 'US',
        displayName: 'United States (New York)',
        timeZoneId: 'America/New_York',
    },
};

/** Array of supported timezone IDs */
export const SUPPORTED_TIMEZONE_IDS = Object.values(SUPPORTED_TIMEZONES).map(
    (tz) => tz.timeZoneId
);
